import type { CSSProperties } from 'react';
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';

interface SelectOption<T = string> {
    value: T;
    label: string;
}

interface SelectProps<T = string> {
    label?: string;
    value: T;
    onChange: (value: T) => void;
    options: SelectOption<T>[];
    error?: string;
    helperText?: string;
    /** Limits the number of visible items in the dropdown. Each item is ~40px tall. */
    maxVisibleItems?: number;
}

export function Select<T = string>({
    label,
    value,
    onChange,
    options,
    error,
    helperText,
    maxVisibleItems,
}: SelectProps<T>) {
    const selected = options.find((opt) => opt.value === value);

    return (
        <div className="w-full">
            {label && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {label}
                </label>
            )}
            <Listbox value={value} onChange={onChange}>
                <div className="relative">
                    <Listbox.Button
                        className={`
                            relative w-full rounded-lg border py-2 pl-3 pr-10 text-left
                            focus:outline-hidden focus:ring-2
                            ${
                                error
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                            }
                            bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                        `}
                    >
                        <span className="block truncate">
                            {selected?.label || 'Select...'}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon
                                className="h-5 w-5 text-gray-400"
                                aria-hidden="true"
                            />
                        </span>
                    </Listbox.Button>
                    {/*
                      `anchor` renders the list in a portal next to the button, so a parent
                      with `overflow-hidden` (the Dialog panel) cannot clip it, and the list
                      flips above the button when there is no room below.
                    */}
                    <Listbox.Options
                        anchor="bottom start"
                        transition
                        className="z-60 w-(--button-width) overflow-auto rounded-lg bg-white shadow-lg ring-1 ring-black/5 [--anchor-gap:4px] focus:outline-hidden data-closed:opacity-0 data-leave:transition data-leave:duration-100 data-leave:ease-in dark:bg-dark dark:ring-gray-600"
                        style={
                            {
                                '--anchor-max-height': maxVisibleItems
                                    ? `${maxVisibleItems * 40}px`
                                    : '240px',
                            } as CSSProperties
                        }
                    >
                        {options.map((option, index) => (
                            <Listbox.Option
                                key={String(option.value)}
                                value={option.value}
                                className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                        active
                                            ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                                            : 'text-gray-900 dark:text-gray-100'
                                    } ${index === 0 ? 'rounded-t-lg' : ''} ${index === options.length - 1 ? 'rounded-b-lg' : ''}`
                                }
                            >
                                {({ selected }) => (
                                    <>
                                        <span
                                            className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                        >
                                            {option.label}
                                        </span>
                                        {selected && (
                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600 dark:text-blue-400">
                                                <CheckIcon
                                                    className="h-5 w-5"
                                                    aria-hidden="true"
                                                />
                                            </span>
                                        )}
                                    </>
                                )}
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </div>
            </Listbox>
            {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
            {helperText && !error && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
            )}
        </div>
    );
}
